import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { OnboardingDocument } from 'app/profile/models/onboarding.model';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import { I18nService } from 'app/shared/services/i18n.service';
import { DocumentsGridComponent } from '../documents/documents-grid/documents-grid.component';
import { OPERATION, SupplierMSME } from 'app/profile/models/supplier.model';
import { DecrementLoaderCount, IncrementLoaderCount } from 'app/shared/store/actions';
import { OnboardingService } from 'app/profile/services/onboarding.service';
import { validateAllFormFields } from 'app/shared/utils/utils';
import { combineLatest } from 'rxjs';
import { map, filter } from 'rxjs/operators';

@Component({
    selector: 'app-msme',
    templateUrl: './msme.component.html',
    styleUrls: ['./msme.component.scss'],
})
export class MsmeComponent implements OnInit {
    /** Determines if the controls should be disabled */
    @Input('tsl-readOnly') readOnly = false;

    /** Required supplier code to be provided by onboaridng parent component */
    @Input('tsl-supplierCode') supplierCode: string = '';

    /** Determines if we hide the save button, so that the continue observable is the one that will trigger the submit action */
    @Input('tsl-hideSaveButton') hideSaveBtn: boolean = false;

    /** Determines if we hide the save button, so that the continue observable is the one that will trigger the submit action */
    @Input('tsl-taskConfiguration') taskConfiguration: any;

    /** Determines if we hide the save button, so that the continue observable is the one that will trigger the submit action */
    @Input('tsl-existingValue') existingValue: SupplierMSME = null;

    /** Address document data from document step status */
    @Input('tsl-requiredDocuments') requiredDocuments: OnboardingDocument[];

    /** Event to trigger when the form is submitted with valid data */
    @Output('tsl-onSave') onSubmitEvent: EventEmitter<{ msmeDetails: SupplierMSME }> = new EventEmitter<{
        msmeDetails: SupplierMSME;
    }>();

    /** Event to trigger when user submits a form with errors */
    @Output('tsl-onSaveError') onErrorEvent: EventEmitter<{ errors: ValidationErrors[]; errorMessage: string }> =
        new EventEmitter<{ errors: ValidationErrors[]; errorMessage: string }>();

    /** Reference to the documents grid in the template */
    @ViewChild('msmeDocumentsGrid', { static: false }) documentsGrid: DocumentsGridComponent;

    form: FormGroup;
    config: any;
    isMSMEEligible: boolean = false;
    confirmControl: FormControl;
    constructor(
        private translate: I18nService,
        private store: Store<SupplierState>,
        private fb: FormBuilder,
        private onboardingService: OnboardingService,
    ) {}

    ngOnInit(): void {
        // get form config
        if (this.taskConfiguration && this.taskConfiguration.RemitToCountry) {
            this.store.dispatch(new IncrementLoaderCount());
            this.onboardingService.getMsmeFieldConfig(this.taskConfiguration.RemitToCountry).subscribe((res) => {
                if (res) {
                    this.config = res;
                    this.form = this.createFormGroup(this.config);
                    this.setupVisibilityConditions();
                    // add confirm radio button
                    this.addConfirmRadioButton();

                    // add form values
                    this.appendFormValues();
                }
                this.store.dispatch(new DecrementLoaderCount());
            });
        } else {
            console.log('Task Configuration missing.');
        }
    }

    private appendFormValues(){
      if(this.existingValue){
        this.isMSMEEligible = this.existingValue.MSMEEligible;
        this.form.get('confirmControl').setValue(this.existingValue.Acknowledge ? "true" : null);
        this.form.get('confirmControl').updateValueAndValidity();
        if(this.existingValue.Response){
          this.config.forEach(x => {
             if(this.existingValue.Response[x.label]){
                this.form.get(x.controlName).setValue(this.existingValue.Response[x.label]);
                this.form.get(x.controlName).updateValueAndValidity();
             }
          });
        }

        if(this.existingValue?.DocumentInfos){

          // Additional check to not override required document field if no uploaded documents
          if(this.existingValue?.DocumentInfos?.length > 0)
          {
              this.requiredDocuments = this.existingValue.DocumentInfos;
          }
      }
      }
    }

    private createFormGroup(controlsConfig: any[]): FormGroup {
        const group = this.fb.group({});
        controlsConfig.forEach((config) => {
            const validators = config.optional == true ? [] : this.createValidators(config.validations);
            group.addControl(config.controlName, this.fb.control('', validators));
        });
        return group;
    }

    private addConfirmRadioButton(){
      this.confirmControl = new FormControl();
      if(this.existingValue?.Acknowledge == true){
        this.confirmControl.setValue(this.existingValue?.Acknowledge);
        this.confirmControl.updateValueAndValidity();
      }
      this.confirmControl.setValidators([Validators.required]);
      this.form.addControl('confirmControl',this.confirmControl);
    }

    private canShowDynamicControl(config){
        if(config && config?.optional == true && this.isMSMEEligible == false){
            return false;
        }
        return true;
    }

    private createValidators(validations: any): ValidatorFn[] {
        const validators: ValidatorFn[] = [];

        if (validations?.required?.enabled) {
            validators.push(Validators.required);
        }

        if (validations?.pattern?.regex) {
            validators.push(Validators.pattern(validations.pattern.regex));
        }

        return validators;
    }

    private setupVisibilityConditions() {
        const nonOptionalControls = this.config
            .filter((f) => !f.optional)
            .map((f) => this.form.get(f.controlName).valueChanges);

        combineLatest(nonOptionalControls)
            .pipe(
                map(() => this.config.filter((f) => !f.optional).every((f) => this.form.get(f.controlName).valid)),
                filter((valid) => valid),
            )
            .subscribe(() => {
                this.checkOptionalRequirements();
            });
    }

    private checkOptionalRequirements() {
        const nonOptionalValues = this.config
            .filter((f) => !f.optional)
            .reduce(
                (acc, field) => ({
                    ...acc,
                    [field.label]: this.form.get(field.controlName).value,
                }),
                {},
            );

        this.store.dispatch(new IncrementLoaderCount());
        this.onboardingService
            .isMsmeEligible(this.taskConfiguration.RemitToCountry, nonOptionalValues)
            .subscribe((res) => {
                var optionalFields = this.config.filter((x) => x.optional == true);
                if (optionalFields) {
                    optionalFields.forEach((field) => {
                        let validators = res == true ? this.createValidators(field.validations) : null;
                        this.form.get(field.controlName).setValidators(validators);
                        this.form.get(field.controlName).updateValueAndValidity();
                    });
                }
                this.isMSMEEligible = res;
                this.store.dispatch(new DecrementLoaderCount());
            });
    }

    submit() {
        if (this.form.valid) {
            //  Check if user has provided documents if required
            if (this.documentsGrid && this.requiredDocuments?.length > 0 && this.isMSMEEligible) {
                this.documentsGrid.isValid().subscribe((val) => {
                    if (val === true) {
                        this.processSubmit();
                    }
                });
            } else {
                this.processSubmit();
            }
        } else {
            validateAllFormFields(this.form);
            if (this.documentsGrid && this.requiredDocuments?.length > 0 && this.isMSMEEligible) {
                //  Trigger validation to show error messages
                this.documentsGrid.isValid();
            }
        }
    }

    processSubmit() {
        //  Trigger onSubmit event
        let msmeDetails = new SupplierMSME();
        msmeDetails.MSMEEligible = this.isMSMEEligible;
        msmeDetails.Response = msmeDetails
        msmeDetails.Operation = OPERATION.CREATE;
        msmeDetails.RemitToCountry = this.taskConfiguration.RemitToCountry;
        const formValues = this.config
            .reduce(
                (acc, field) => ({
                    ...acc,
                    [field.label]: this.form.get(field.controlName).value,
                }),
                {},
            );
        msmeDetails.Response = formValues;

        //  Append uploaded documents to address object
        let msmeDocuments =
            this.documentsGrid &&
            this.documentsGrid.documents &&
            this.documentsGrid.documents?.filter(
                (x) => x.FileName != undefined && x.FileName != null && x.FileName.length > 0,
            )?.length > 0
                ? this.documentsGrid.documents
                      .filter((x) => x.FileName != undefined && x.FileName != null && x.FileName.length > 0)
                      .map((doc) => ({ ...doc, FormGroup: null }))
                : [];
        msmeDetails.DocumentInfos = msmeDocuments;
        msmeDetails.Documents = msmeDocuments.map((doc) => doc.DocumentID);
        msmeDetails.StageID = this.existingValue?.StageID || null;
        msmeDetails.Acknowledge = this.form.get('confirmControl').value;

        this.onSubmitEvent.emit({ msmeDetails: msmeDetails });
    }
}
